/* ==========================================================================
   Ruts Atelier — Carrusel compartido (Universo / Opiniones)
   Vanilla JS, sin librerías externas. Una instancia por sección.

   - Autoplay continuo y lineal via requestAnimationFrame (sin saltos).
   - scroll-snap SOLO cuando el autoplay está inactivo (evita tirones).
   - Drag con ratón en escritorio, swipe nativo en táctil.
   - Pausa en pointerdown (drag real) / touchstart / focus;
     reanuda tras ~3 s de inactividad.
   - NOTA: no se pausa en mouseenter/mouseleave. Ese trigger se retiró:
     al hacer scroll con el cursor quieto, el navegador dispara mouseenter
     igualmente (cambia el elemento bajo el puntero aunque el puntero no se
     mueva), y como el puntero nunca "sale" si no se mueve, el autoplay se
     quedaba pausado para siempre en carga normal, sin ningún error. Esa era
     la causa raíz del carrusel estático en primer plano. El pointerdown/drag
     y el touch siguen pausando con normalidad.
   - Bucle infinito con clones: aria-hidden="true", sin foco de teclado.
   - Respeta prefers-reduced-motion: reduce (autoplay desactivado).
   - Reanuda al recuperar visibilidad de pestaña (protección secundaria;
     no es la causa del fallo en primer plano, ver nota arriba).
   ========================================================================== */
(function () {
  'use strict';
  if (window.RutsCarousel) return;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var RESUME_DELAY = 3000;

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  function RutsCarousel(root) {
    this.root = root;
    this.track = root.querySelector('[data-ruts-track]');
    if (!this.track || !this.track.children.length) return;

    this.autoplayWanted = root.getAttribute('data-autoplay') === 'true';
    this.speed = parseFloat(root.getAttribute('data-speed')) || 30;
    this.dir = root.getAttribute('data-direction') === 'right' ? -1 : 1;
    this.loop = root.getAttribute('data-loop') === 'true';

    this.pos = 0;
    this.setWidth = 0;
    this.rafId = null;
    this.lastTs = null;
    this.resumeTimer = null;
    this.dragging = false;
    this.dragMoved = false;
    this.dragStartX = 0;
    this.dragStartScroll = 0;

    this.originals = Array.prototype.slice.call(this.track.children);
    if (this.loop) this.buildClones();

    this.bind();
    this.measure();
    if (this.loop && this.dir < 0) {
      // Dirección derecha: empezar en el segundo set para poder retroceder.
      this.track.scrollLeft = this.setWidth;
    }
    this.applyMode();
  }

  /* Clona el set original al final para el bucle sin corte visible.
     Los clones quedan fuera del árbol de accesibilidad y del tab-order. */
  RutsCarousel.prototype.buildClones = function () {
    var frag = document.createDocumentFragment();
    this.originals.forEach(function (item) {
      var clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-ruts-clone', '');
      clone.removeAttribute('id');
      if (clone.matches('a, button')) clone.setAttribute('tabindex', '-1');
      Array.prototype.forEach.call(
        clone.querySelectorAll('a, button, input, select, textarea, [tabindex]'),
        function (el) { el.setAttribute('tabindex', '-1'); }
      );
      Array.prototype.forEach.call(clone.querySelectorAll('[id]'), function (el) {
        el.removeAttribute('id');
      });
      Array.prototype.forEach.call(clone.querySelectorAll('img'), function (img) {
        img.setAttribute('loading', 'lazy');
      });
      frag.appendChild(clone);
    });
    this.track.appendChild(frag);
  };

  RutsCarousel.prototype.measure = function () {
    if (this.loop) {
      var firstClone = this.track.querySelector('[data-ruts-clone]');
      this.setWidth = firstClone
        ? firstClone.offsetLeft - this.originals[0].offsetLeft
        : 0;
    } else {
      this.setWidth = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
    }
  };

  RutsCarousel.prototype.autoplayActive = function () {
    return this.autoplayWanted && !REDUCED.matches;
  };

  /* Snap solo con autoplay inactivo; con autoplay, movimiento lineal puro. */
  RutsCarousel.prototype.applyMode = function () {
    var active = this.autoplayActive();
    this.track.classList.toggle('is-snapping', !active);
    if (active) this.start();
    else this.stop();
  };

  RutsCarousel.prototype.start = function () {
    if (this.rafId || !this.autoplayActive()) return;
    this.lastTs = null;
    this.rafId = requestAnimationFrame(this.tick.bind(this));
  };

  RutsCarousel.prototype.stop = function () {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.lastTs = null;
  };

  RutsCarousel.prototype.tick = function (ts) {
    if (this.lastTs === null) {
      this.lastTs = ts;
      this.pos = this.track.scrollLeft; // re-sincroniza tras interacción manual
    }
    var dt = Math.min((ts - this.lastTs) / 1000, 0.1);
    this.lastTs = ts;

    var span = this.loop
      ? this.setWidth
      : Math.max(this.track.scrollWidth - this.track.clientWidth, 0);

    if (span > 1) {
      // Velocidad = un ciclo completo cada `speed` segundos, lineal.
      // `pos` es float interno: evita el redondeo de scrollLeft a bajas velocidades.
      this.pos += this.dir * (span / this.speed) * dt;
      if (this.loop) {
        if (this.pos >= this.setWidth) this.pos -= this.setWidth;
        else if (this.pos < 0) this.pos += this.setWidth;
      } else {
        if (this.dir > 0 && this.pos >= span) this.pos = 0;
        else if (this.dir < 0 && this.pos <= 0) this.pos = span;
      }
      this.track.scrollLeft = this.pos;
    }
    this.rafId = requestAnimationFrame(this.tick.bind(this));
  };

  RutsCarousel.prototype.pause = function () {
    clearTimeout(this.resumeTimer);
    this.stop();
  };

  RutsCarousel.prototype.scheduleResume = function () {
    if (!this.autoplayActive()) return;
    clearTimeout(this.resumeTimer);
    var self = this;
    this.resumeTimer = setTimeout(function () { self.start(); }, RESUME_DELAY);
  };

  RutsCarousel.prototype.bind = function () {
    var self = this;
    var track = this.track;

    this.onEnter = function () { self.pause(); };
    this.onLeave = function () { self.scheduleResume(); };
    /* mouseenter/mouseleave NO se usan como disparador: ver nota de cabecera.
       Se conservan touch/focus como interacción real. */
    track.addEventListener('touchstart', this.onEnter, { passive: true });
    track.addEventListener('touchend', this.onLeave, { passive: true });
    track.addEventListener('touchcancel', this.onLeave, { passive: true });
    this.root.addEventListener('focusin', this.onEnter);
    this.root.addEventListener('focusout', this.onLeave);

    /* Drag con ratón (el swipe táctil lo hace el scroll nativo) */
    this.onDown = function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      self.dragging = true;
      self.dragMoved = false;
      self.dragStartX = e.clientX;
      self.dragStartScroll = track.scrollLeft;
      self.pause();
      track.classList.add('is-dragging');
      e.preventDefault();
    };
    this.onMove = function (e) {
      if (!self.dragging) return;
      var dx = e.clientX - self.dragStartX;
      if (Math.abs(dx) > 4) self.dragMoved = true;
      track.scrollLeft = self.dragStartScroll - dx;
    };
    this.onUp = function () {
      if (!self.dragging) return;
      self.dragging = false;
      track.classList.remove('is-dragging');
      self.scheduleResume();
    };
    track.addEventListener('pointerdown', this.onDown);
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);

    /* Un drag no debe disparar el clic del enlace */
    this.onClick = function (e) {
      if (self.dragMoved) {
        e.preventDefault();
        e.stopPropagation();
        self.dragMoved = false;
      }
    };
    track.addEventListener('click', this.onClick, true);

    /* Bucle también durante scroll manual (swipe/drag) */
    this.onScroll = function () {
      if (!self.loop || self.setWidth <= 0 || self.rafId) return;
      var sl = track.scrollLeft;
      if (sl >= self.setWidth) {
        track.scrollLeft = sl - self.setWidth;
        self.dragStartScroll -= self.setWidth;
      }
    };
    track.addEventListener('scroll', this.onScroll, { passive: true });

    this.onResize = debounce(function () { self.measure(); }, 150);
    window.addEventListener('resize', this.onResize);
    track.addEventListener('load', this.onResize, true); // imágenes cargadas

    this.onMotionChange = function () { self.applyMode(); };
    if (REDUCED.addEventListener) REDUCED.addEventListener('change', this.onMotionChange);
    else if (REDUCED.addListener) REDUCED.addListener(this.onMotionChange);

    /* Protección secundaria: reanuda al volver a una pestaña oculta.
       No es la causa del fallo en primer plano — ver nota de cabecera. */
    this.onVisibility = function () {
      if (document.visibilityState === 'visible') {
        self.lastTs = null;
        self.applyMode();
      } else {
        self.stop();
      }
    };
    document.addEventListener('visibilitychange', this.onVisibility);
  };

  RutsCarousel.prototype.destroy = function () {
    this.pause();
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (REDUCED.removeEventListener) REDUCED.removeEventListener('change', this.onMotionChange);
    else if (REDUCED.removeListener) REDUCED.removeListener(this.onMotionChange);
    Array.prototype.forEach.call(
      this.track.querySelectorAll('[data-ruts-clone]'),
      function (c) { c.remove(); }
    );
    delete this.root.__rutsCarousel;
  };

  function init(scope) {
    var nodes = (scope || document).querySelectorAll('[data-ruts-carousel]');
    Array.prototype.forEach.call(nodes, function (el) {
      if (!el.__rutsCarousel) el.__rutsCarousel = new RutsCarousel(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* Soporte del editor de temas de Shopify */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector ? e.target.querySelector('[data-ruts-carousel]') : null;
    if (el && el.__rutsCarousel) el.__rutsCarousel.destroy();
  });

  window.RutsCarousel = RutsCarousel;
})();
